import * as React from "react";
import {
    Button,
    Card,
    Field,
    Input,
    MessageBar,
    MessageBarBody,
    MessageBarTitle,
    Select,
    Spinner,
    Subtitle1,
    Subtitle2,
    Text,
} from "@fluentui/react-components";
import { ConfirmDialog } from "../../components/redesign/ConfirmDialog";
import { listAppUsers, userDisplay, type AppUser } from "../../services/userService";
import {
    remarkableFehlertext,
    remarkableService,
    type BereichZustand,
    type RemarkablePruefung,
    type RemarkableZustand,
} from "../../services/remarkableService";

/** Seite, auf der man im reMarkable-Konto den Einmal-Code erzeugt. */
const CODE_SEITE = "https://my.remarkable.com/device/browser/connect";
const CODE = /^[a-z]{8}$/i;

/** Nebentext – dieselbe Farbe wie in den übrigen Einstellungs-Abschnitten. */
const NEBENTEXT = "#6b5a66";
const KARTE: React.CSSProperties = { padding: 24, display: "flex", flexDirection: "column", gap: 12 };

const datum = (iso: string | null) => (iso ? new Date(iso).toLocaleString("de-DE") : "noch nie");

/** Läuft der Bereich, steht das schlicht da; ist er gestört, ist es ein roter Hinweis mit Grund. */
const BereichZeile: React.FC<{ titel: string; z: BereichZustand }> = ({ titel, z }) =>
    z.zustand === "ok" ? (
        <Text block>{`${titel}: läuft`}</Text>
    ) : (
        <MessageBar intent="error">
            <MessageBarBody>
                <MessageBarTitle>{`${titel} gestört${z.seit ? ` seit ${datum(z.seit)}` : ""}`}</MessageBarTitle>
                {z.fehler}
            </MessageBarBody>
        </MessageBar>
    );

/**
 * Admin-Abschnitt „reMarkable“ (Entwurf 2026-10-23-remarkable-notizen-design.md):
 * ein reMarkable-Konto per Einmal-Code mit einem KMVS-Nutzer verbinden, den
 * Zustand von Anlegen und Abholen sehen, prüfen und trennen.
 */
export const RemarkableSection: React.FC = () => {
    const [zustand, setZustand] = React.useState<RemarkableZustand | null>(null);
    const [nutzer, setNutzer] = React.useState<AppUser[]>([]);
    const [userId, setUserId] = React.useState("");
    const [code, setCode] = React.useState("");
    const [beschaeftigt, setBeschaeftigt] = React.useState(false);
    const [meldung, setMeldung] = React.useState<string | null>(null);
    const [fehler, setFehler] = React.useState<string | null>(null);
    const [trennenFuer, setTrennenFuer] = React.useState<string | null>(null);
    const rueckmeldungRef = React.useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
        let aktiv = true;
        remarkableService
            .zustand()
            .then((z) => aktiv && setZustand(z))
            .catch((e) => aktiv && setFehler(remarkableFehlertext(e)));
        void listAppUsers().then((u) => aktiv && setNutzer(u));
        return () => {
            aktiv = false;
        };
    }, []);

    // Ein Abgleich läuft im Hintergrund und kann Minuten dauern: Stand nachziehen, bis er fertig ist.
    const laeuft = !!zustand?.verbindungen.some((v) => v.abgleichLaeuft);
    React.useEffect(() => {
        if (!laeuft) return undefined;
        const uhr = window.setInterval(() => {
            remarkableService
                .zustand()
                .then(setZustand)
                .catch(() => undefined);
        }, 5000);
        return () => window.clearInterval(uhr);
    }, [laeuft]);

    // Die Rückmeldung steht oben; wer unten auf „Verbinden“ geklickt hat, soll sie sehen.
    React.useEffect(() => {
        if (meldung || fehler) rueckmeldungRef.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
    }, [meldung, fehler]);

    const name = (id: string) => {
        const u = nutzer.find((n) => n.id === id);
        return u ? userDisplay(u) : id;
    };

    const ausfuehren = async (aktion: () => Promise<RemarkablePruefung | RemarkableZustand>) => {
        setBeschaeftigt(true);
        setMeldung(null);
        setFehler(null);
        try {
            const r = await aktion();
            if ("zustand" in r) {
                setZustand(r.zustand);
                if (r.ok) setMeldung(r.meldung);
                else setFehler(r.meldung);
            } else {
                setZustand(r);
            }
        } catch (e) {
            setFehler(remarkableFehlertext(e));
        } finally {
            setBeschaeftigt(false);
        }
    };

    const eingerichtet = zustand?.eingerichtet ?? false;
    const codeBereinigt = code.trim();
    const bereitsVerbunden = new Set(zustand?.verbindungen.filter((v) => v.verbunden).map((v) => v.userId));

    return (
        <section aria-label="reMarkable" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Card style={KARTE}>
                <Subtitle1 as="h2">reMarkable</Subtitle1>
                <Text block style={{ color: NEBENTEXT }}>
                    KMVS legt auf dem reMarkable eines Mitarbeiters im Ordner „KMVS“ Notizbücher für Kundentermine und
                    Vorgänge an und holt die Handschrift zurück ins CRM. Verbunden wird das reMarkable-Konto über einen
                    Einmal-Code.
                </Text>

                {zustand === null && !fehler && <Spinner label="Wird geladen…" />}

                {zustand && !eingerichtet && (
                    <MessageBar intent="warning">
                        <MessageBarBody>
                            <MessageBarTitle>reMarkable ist auf diesem Server nicht eingerichtet.</MessageBarTitle>
                            Der Server-Administrator muss in der Server-Konfiguration den Schlüssel{" "}
                            <code>KMVS_REMARKABLE_SCHLUESSEL</code> hinterlegen. Bis dahin lässt sich kein Konto
                            verbinden.
                        </MessageBarBody>
                    </MessageBar>
                )}
            </Card>

            {(meldung || fehler || beschaeftigt) && (
                <div ref={rueckmeldungRef} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {beschaeftigt && <Spinner size="small" label="Einen Moment …" />}
                    {meldung && (
                        <MessageBar intent="success" role="status">
                            <MessageBarBody>{meldung}</MessageBarBody>
                        </MessageBar>
                    )}
                    {fehler && (
                        <MessageBar intent="error" role="alert">
                            <MessageBarBody>{fehler}</MessageBarBody>
                        </MessageBar>
                    )}
                </div>
            )}

            {zustand?.verbindungen.map((v) => (
                <Card key={v.userId} style={KARTE}>
                    <Subtitle2 as="h3">{name(v.userId)}</Subtitle2>
                    {v.verbunden ? (
                        <>
                            <Text block style={{ color: NEBENTEXT }}>
                                {`Verbunden seit ${datum(v.verbundenAm)}`}
                            </Text>
                            <BereichZeile titel="Anlegen" z={v.anlegen} />
                            <BereichZeile titel="Abholen" z={v.abholen} />
                            <Text block>
                                {`Notizbücher: ${v.notizbuecher.aktiv} aktiv · ${v.notizbuecher.archiviert} im Archiv · ${v.notizbuecher.nurGeraet} nur noch auf dem Gerät`}
                            </Text>
                            <Text block style={{ color: NEBENTEXT }}>
                                {`Letzter Abgleich: ${datum(v.letzterAbgleich)} · Letzte Abholung: ${datum(v.letzteAbholung)}`}
                            </Text>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                <Button
                                    appearance="primary"
                                    disabled={beschaeftigt || !eingerichtet || v.abgleichLaeuft}
                                    onClick={() => void ausfuehren(() => remarkableService.abgleichen(v.userId))}
                                >
                                    {v.abgleichLaeuft ? "Abgleich läuft …" : "Jetzt abgleichen"}
                                </Button>
                                <Button
                                    disabled={beschaeftigt || !eingerichtet || v.abgleichLaeuft}
                                    onClick={() => void ausfuehren(() => remarkableService.pruefen(v.userId))}
                                >
                                    Verbindung prüfen
                                </Button>
                                <Button disabled={beschaeftigt} onClick={() => setTrennenFuer(v.userId)}>
                                    Trennen
                                </Button>
                            </div>
                        </>
                    ) : (
                        <>
                            <Text block>Getrennt – KMVS legt nichts an und holt nichts ab.</Text>
                            <Text block style={{ color: NEBENTEXT }}>
                                Zum erneuten Verbinden unter „Konto verbinden“ einen neuen Code eintragen.
                            </Text>
                        </>
                    )}
                </Card>
            ))}

            <Card style={KARTE}>
                <Subtitle2 as="h3">Konto verbinden</Subtitle2>
                <Text block style={{ color: NEBENTEXT }}>
                    Im reMarkable-Konto des Mitarbeiters auf{" "}
                    <a href={CODE_SEITE} target="_blank" rel="noopener noreferrer">
                        my.remarkable.com
                    </a>{" "}
                    einen Code erzeugen und hier eintragen. Der Code gilt nur wenige Minuten.
                </Text>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
                    <Field label="KMVS-Nutzer" hint="Der Mitarbeiter, dessen reMarkable verbunden wird.">
                        <Select value={userId} onChange={(_, d) => setUserId(d.value)}>
                            <option value="">Bitte wählen</option>
                            {nutzer.map((u) => (
                                <option key={u.id} value={u.id}>
                                    {`${userDisplay(u)}${bereitsVerbunden.has(u.id) ? " (bereits verbunden)" : ""}`}
                                </option>
                            ))}
                        </Select>
                    </Field>
                    <Field label="Code" hint="Acht Buchstaben, genau so, wie reMarkable sie anzeigt.">
                        <Input
                            value={code}
                            onChange={(_, d) => setCode(d.value)}
                            maxLength={10}
                            spellCheck={false}
                            autoCapitalize="off"
                            autoComplete="off"
                            placeholder="abcdefgh"
                        />
                    </Field>
                </div>
                <div>
                    <Button
                        appearance="primary"
                        disabled={beschaeftigt || !eingerichtet || !userId || !CODE.test(codeBereinigt)}
                        onClick={() =>
                            void ausfuehren(async () => {
                                const r = await remarkableService.verbinden(userId, codeBereinigt);
                                setCode("");
                                return r;
                            })
                        }
                    >
                        Verbinden
                    </Button>
                </div>
            </Card>

            <ConfirmDialog
                open={trennenFuer !== null}
                title="reMarkable trennen?"
                message={
                    <>
                        <p style={{ margin: "0 0 8px" }}>
                            Die Verbindung von <b>{name(trennenFuer ?? "")}</b> wird getrennt.
                        </p>
                        <p style={{ margin: 0 }}>
                            KMVS holt dann nichts mehr ab und legt nichts mehr an. Was schon im CRM steht, bleibt, ebenso
                            die Notizbücher auf dem Gerät. Wird später wieder verbunden, macht KMVS dort weiter, ohne etwas
                            doppelt anzulegen.
                        </p>
                        <p style={{ margin: "8px 0 0" }}>
                            Den Zugang widerruft das nicht bei reMarkable – dafür KMVS zusätzlich auf my.remarkable.com bei
                            den verbundenen Geräten entfernen.
                        </p>
                    </>
                }
                confirmLabel="Trennen"
                tone="danger"
                onCancel={() => setTrennenFuer(null)}
                onConfirm={() => {
                    const id = trennenFuer;
                    setTrennenFuer(null);
                    if (id) void ausfuehren(() => remarkableService.trennen(id));
                }}
            />
        </section>
    );
};
